import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/** How long the shipping progress fill takes to travel to its new value, in milliseconds. */
const PROGRESS_DURATION = 600;

/**
 * @param {number} value
 * @returns {number} `value` clamped to the 0…1 range, or 0 when it is not a number.
 */
function clampRatio(value) {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/**
 * A custom element that manages cart drawer 2.
 *
 * Starts as a copy of `cart-drawer.js` registered under its own tag name, so the behaviour of
 * drawer 2 can be changed without touching the default drawer. On top of the default behaviour it
 * animates the free shipping progress bar and unlocks each milestone as the fill reaches it.
 *
 * @extends {DialogComponent}
 */
class CartDrawer2Component extends DialogComponent {
  /** The fill ratio currently painted, so an interrupted animation resumes from where it is. */
  #progressRatio = 0;

  /**
   * The last ratio the *server* rendered, as opposed to what is painted. Crossings are measured
   * against this so that opening the drawer — which animates up from an empty bar — never
   * celebrates a threshold the customer had already cleared. Null until first seeded.
   * @type {number | null}
   */
  #lastServerRatio = null;

  /** @type {number | null} */
  #progressFrame = null;

  /** @type {MutationObserver | null} */
  #progressObserver = null;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // A fresh controller every time: the section renderer disconnects and reconnects this element,
    // and re-using an aborted signal would silently drop every listener below.
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleSubscribeClick, { signal });
    this.addEventListener('change', this.#handleSellingPlanChange, { signal });

    // The theme morphs the whole header section after a cart change, which re-renders the track with
    // a new `data-progress`. Watching for that keeps the animation independent of cart events.
    this.#progressObserver = new MutationObserver(this.#handleProgressMutation);
    this.#progressObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-progress'],
    });

    // Seeded once per element instance. A morph keeps the instance alive, so the value carries
    // across cart changes; a genuine replacement re-seeds from the new DOM and simply skips one
    // celebration, which is the safe direction to fail in.
    if (this.#lastServerRatio === null) {
      this.#lastServerRatio = clampRatio(Number(this.#track?.getAttribute('data-progress')));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#controller.abort();

    this.#progressObserver?.disconnect();
    this.#progressObserver = null;

    if (this.#progressFrame) cancelAnimationFrame(this.#progressFrame);
    this.#progressFrame = null;
  }

  #handleCartAdd = () => {
    // The progress bar itself is handled by the mutation observer, whether the drawer opens or not.
    if (this.hasAttribute('auto-open')) this.open();
  };

  #handleProgressMutation = () => {
    const target = clampRatio(Number(this.#track?.getAttribute('data-progress')));
    const previous = this.#lastServerRatio;

    this.#lastServerRatio = target;

    // A null previous means this element has not seen a server value yet, so there is no crossing
    // to speak of — passing the target itself selects nothing.
    this.animateShippingProgress(undefined, { celebrateFrom: previous ?? target });
  };

  /**
   * Handles the two buttons the drawer owns: an upsell ADD, and "subscribe and save" on a line that
   * has no selling plan yet.
   * @param {MouseEvent} event
   */
  #handleSubscribeClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const upsell = target.closest('[data-upsell-add]');

    if (upsell instanceof HTMLElement) {
      this.addUpsellToCart(upsell);
      return;
    }

    const button = target.closest('[data-subscribe-line]');

    if (!(button instanceof HTMLElement)) return;

    this.setSellingPlan(button, {
      line: button.dataset.subscribeLine,
      quantity: button.dataset.quantity,
      sellingPlan: button.dataset.sellingPlan,
    });
  };

  open() {
    // A closed drawer has never shown its progress bar, so it fills from empty; an open one carries
    // on from whatever is on screen.
    const wasOpen = this.querySelector('dialog')?.open === true;

    this.showDialog();

    // `celebrateFrom: 1` sits above every milestone, so merely opening the drawer never fires an
    // unlock. Real crossings arrive through the mutation observer instead.
    this.animateShippingProgress(wasOpen ? undefined : 0, { celebrateFrom: 1 });

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /** @returns {string | null} The section the drawer lives in, needed to re-render it. */
  get #sectionId() {
    return this.querySelector('cart-items-component')?.getAttribute('data-section-id') ?? null;
  }

  /**
   * Adds an upsell product to the cart and lets the theme repaint the drawer.
   * @param {HTMLElement} button - The ADD button that was pressed.
   */
  async addUpsellToCart(button) {
    const variantId = Number(button.dataset.upsellAdd);

    if (!variantId) return;

    const sectionId = this.#sectionId;

    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error(`Add to cart failed with ${response.status}`);

      const result = await response.json();

      this.#announceCartChange(result.sections, sectionId);
    } catch (error) {
      console.error('[cart-drawer-2]', error);
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Tells the theme the cart changed, handing over the freshly rendered section so the cart items
   * component morphs it instead of asking the Section Rendering API for it again.
   *
   * @param {Record<string, string> | undefined} sections
   * @param {string | null} sectionId
   */
  #announceCartChange(sections, sectionId) {
    // The cart API's add endpoint returns the line items, not the cart, so the new count is read
    // back out of the rendered section — the same place the theme's own cart component reads it.
    const html = sectionId ? sections?.[sectionId] : undefined;
    const rendered = html ? new DOMParser().parseFromString(html, 'text/html') : null;
    const count = rendered?.querySelector('[ref="cartItemCount"]')?.textContent;

    document.dispatchEvent(
      new CartUpdateEvent({}, 'cart-drawer-2', {
        source: 'cart-drawer-2',
        itemCount: count ? parseInt(count, 10) : undefined,
        sections,
      })
    );
  }

  /**
   * Swapping the frequency — or picking "One-time purchase" — rewrites the line's selling plan.
   * @param {Event} event
   */
  #handleSellingPlanChange = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement) || !select.dataset.sellingPlanLine) return;

    this.setSellingPlan(select, {
      line: select.dataset.sellingPlanLine,
      quantity: select.dataset.quantity,
      sellingPlan: select.value,
    });
  };

  /**
   * Writes a line's selling plan through the cart API and lets the theme re-render the drawer.
   *
   * The cart API only accepts `line` (not the line key) when `selling_plan` is set, and wants the
   * quantity spelled out every time.
   *
   * @param {HTMLElement} source - The control that triggered the change, used for the busy state.
   * @param {object} change
   * @param {string} [change.line] - The 1-based line number.
   * @param {string} [change.quantity] - The line's current quantity.
   * @param {string} [change.sellingPlan] - The selling plan id, or an empty string to remove it.
   */
  async setSellingPlan(source, { line, quantity, sellingPlan }) {
    const lineNumber = Number(line);

    if (!lineNumber) return;

    const row = source.closest('[data-key]') ?? source;
    const sectionId = this.#sectionId;

    row.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: lineNumber,
          quantity: Number(quantity) || 1,
          selling_plan: sellingPlan ? Number(sellingPlan) : null,
          // Asking for the section in the same request means the drawer repaints straight away
          // instead of waiting on a second round trip to the Section Rendering API.
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error(`Selling plan change failed with ${response.status}`);

      const cart = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-drawer-2', {
          source: 'cart-drawer-2',
          itemCount: cart.item_count,
          sections: cart.sections,
        })
      );
    } catch (error) {
      console.error('[cart-drawer-2]', error);
      row.removeAttribute('aria-busy');
    }
  }

  /** @returns {Element | null} The shipping progress track, when the merchant enabled it. */
  get #track() {
    return this.querySelector('[data-shipping-progress]');
  }

  /**
   * Runs the fill towards the ratio Liquid rendered on the track, unlocking any milestone the fill
   * passes on the way.
   *
   * @param {number} [from] - Ratio to start from. Defaults to whatever is currently painted.
   * @param {{ celebrateFrom?: number }} [options] - Milestones above `celebrateFrom` and at or
   *   below the new target are unlocked as the fill reaches them. Defaults to no celebration.
   */
  animateShippingProgress(from, options) {
    const track = this.#track;
    const fill = track?.querySelector('[data-shipping-progress-fill]');

    if (!(track instanceof HTMLElement) || !(fill instanceof HTMLElement)) return;

    const target = clampRatio(Number(track.dataset.progress));
    const start = clampRatio(from ?? this.#progressRatio);
    const celebrateFrom = options?.celebrateFrom ?? 1;

    // Only forward movement celebrates. Removing an item drops the bar back and should quietly
    // re-lock, not replay the animation in reverse.
    const pending = Array.from(track.querySelectorAll('[data-milestone-ratio]'))
      .map((el) => ({ el, ratio: clampRatio(Number(el.getAttribute('data-milestone-ratio'))) }))
      .filter(({ ratio }) => ratio > celebrateFrom && ratio <= target)
      .sort((a, b) => a.ratio - b.ratio);

    this.#syncMilestoneStates(target);

    if (this.#progressFrame) cancelAnimationFrame(this.#progressFrame);

    // Paint the starting value in the same task as the DOM change that triggered this, so a morph
    // never flashes the final width for a frame.
    this.#paintShippingProgress(start);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (start === target || reducedMotion) {
      this.#paintShippingProgress(target);
      for (const { el } of pending) this.#unlockMilestone(el, reducedMotion);
      return;
    }

    const startedAt = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const elapsed = Math.min(1, (now - startedAt) / PROGRESS_DURATION);
      // easeOutCubic — quick off the mark, settles gently on the target.
      const eased = 1 - Math.pow(1 - elapsed, 3);
      const painted = start + (target - start) * eased;

      this.#paintShippingProgress(painted);

      // Fire each unlock on the frame the fill actually reaches that marker, rather than all at
      // once up front — with two thresholds the customer sees them light in sequence.
      while (pending.length > 0 && painted >= pending[0].ratio) {
        const next = pending.shift();
        if (next) this.#unlockMilestone(next.el, false);
      }

      if (elapsed < 1) {
        this.#progressFrame = requestAnimationFrame(step);
      } else {
        this.#progressFrame = null;
        this.#paintShippingProgress(target);

        for (const { el } of pending) this.#unlockMilestone(el, false);
        pending.length = 0;
      }
    };

    this.#progressFrame = requestAnimationFrame(step);
  }

  /**
   * Brings every milestone's locked/unlocked class in line with a ratio. Handles the downward case:
   * emptying the cart re-locks the markers without animating anything.
   *
   * @param {number} ratio
   */
  #syncMilestoneStates(ratio) {
    const milestones = this.#track?.querySelectorAll('[data-milestone-ratio]') ?? [];

    for (const el of milestones) {
      const at = clampRatio(Number(el.getAttribute('data-milestone-ratio')));

      if (at > ratio) {
        el.classList.remove('is-reached', 'is-unlocking');
      } else {
        el.classList.add('is-reached');
      }
    }
  }

  /**
   * @param {Element} el
   * @param {boolean} reducedMotion
   */
  #unlockMilestone(el, reducedMotion) {
    el.classList.add('is-reached');

    if (reducedMotion) return;

    // Removing and forcing a reflow before re-adding restarts the animation when the same milestone
    // is crossed twice in quick succession — otherwise the second crossing is silent.
    el.classList.remove('is-unlocking');
    void /** @type {HTMLElement} */ (el).offsetWidth;
    el.classList.add('is-unlocking');

    el.addEventListener('animationend', () => el.classList.remove('is-unlocking'), { once: true });

    // Hook for anything else that should fire on unlock — a toast, an analytics event, the "free
    // gift at checkout" line. `goal` is in cents, so a listener can tell the thresholds apart.
    this.dispatchEvent(
      new CustomEvent('cart-drawer-2:milestone-reached', {
        bubbles: true,
        detail: { goal: Number(el.getAttribute('data-milestone-goal')) || null },
      })
    );
  }

  /**
   * @param {number} ratio
   */
  #paintShippingProgress(ratio) {
    const fill = this.#track?.querySelector('[data-shipping-progress-fill]');

    if (!(fill instanceof HTMLElement)) return;

    this.#progressRatio = ratio;
    fill.style.width = `${ratio * 100}%`;
  }
}

if (!customElements.get('cart-drawer-2-component')) {
  customElements.define('cart-drawer-2-component', CartDrawer2Component);
}